// SetPassword — shown after OTP verification when the account has no login
// password yet (verify-otp returned hasPassword=false). Choose a password; on
// success the user is sent to login. Uses the shared reset-password endpoint.
// The phone is carried in via router state.

import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import { Button, Field, PasswordInput, PasswordStrength } from '@/components/common';
import AuthLayout from '@/layout/AuthLayout';
import { useResetPasswordMutation } from '@/api/endpoints/authApi';
import type { ApiErrorShape } from '@/api/types';
import { scorePassword } from '@/utils/password';
import AuthBack from './AuthBack';
import { validateAll, validateField, type FormErrors } from './validation';

const schema = yup.object({
  password: yup
    .string()
    .required('error_password_required')
    .test('strength', 'error_password_weak', (value) => scorePassword(value ?? '') >= 3),
});

interface SetPasswordState {
  dialCode?: string;
  phone?: string;
}

const SetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as SetPasswordState;

  const [resetPassword, { isLoading: submitting }] = useResetPasswordMutation();

  const [values, setValues] = useState({ password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const dialCode = state.dialCode ?? '';
  const phone = state.phone ?? '';

  // The screen is meaningless without a phone from the verify step.
  if (!phone) return <Navigate to='/login' replace />;

  const setValue = (name: 'password', value: string) => {
    const next = { ...values, [name]: value };
    setValues(next);
    if (submitted) {
      validateField(schema, name, next).then((message) =>
        setErrors((prev) => ({ ...prev, [name]: message })),
      );
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const nextErrors = await validateAll(schema, values);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    try {
      await resetPassword({
        countryCode: dialCode,
        mobileNumber: phone,
        password: values.password,
      }).unwrap();
      toast.success(t('toast_password_set'));
      navigate('/login');
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <AuthLayout variant='login'>
      <AuthBack onClick={() => navigate('/login')} />
      <div className='auth-page'>
        <header className='auth-page__header'>
          <h1 className='auth-page__title'>{t('text_set_password_title')}</h1>
          <p className='auth-page__subtitle'>{t('text_set_password_subtitle')}</p>
        </header>

        <form className='auth-page__form' onSubmit={handleSubmit} noValidate>
          <Field
            label={t('field_new_password')}
            htmlFor='sp-password'
            error={errors.password ? t(errors.password) : undefined}
          >
            <PasswordInput
              id='sp-password'
              placeholder={t('field_enter_password')}
              autoComplete='new-password'
              value={values.password}
              onChange={(e) => setValue('password', e.target.value)}
              error={!!errors.password}
            />
            <PasswordStrength password={values.password} />
          </Field>

          <Button type='submit' size='lg' loading={submitting} className='auth-page__submit'>
            {t('button_confirm')}
          </Button>

          <p className='auth-page__alt'>
            <button type='button' className='auth-page__link' onClick={() => navigate('/login')}>
              {t('text_go_to_login')}
            </button>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
};

export default SetPassword;
